import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { companiesApi } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Plus, MagnifyingGlass, Pencil, Buildings, Users, UploadSimple, CaretLeft, CaretRight, Eye, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import BulkUploadDialog from '../../components/BulkUploadDialog';
import DeleteConfirmDialog from '../../components/DeleteConfirmDialog';
import BulkDeleteConfirmDialog from '../../components/BulkDeleteConfirmDialog';
import { validateRut } from '../../utils/rutValidator';
import { Checkbox } from '../../components/ui/checkbox';

export default function CompaniesPage() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const ITEMS_PER_PAGE = 10;
  const [editingCompany, setEditingCompany] = useState(null);
  const [deletingCompany, setDeletingCompany] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    trade_name: '',
    rut: '',
    address: '',
    phone: '',
    email: '',
    is_client: false,
    client_id: ''
  });

  useEffect(() => {
    // Fetch clients for dropdown (one-time, small set)
    companiesApi.getAll({ is_client: true, page_size: 999 }).then(res => {
      setClients((res.data.items || res.data).filter(c => c.is_client));
    }).catch(() => {});
  }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchTerm), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Fetch paginated data from server
  useEffect(() => {
    fetchData();
  }, [currentPage, searchDebounced, typeFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, page_size: ITEMS_PER_PAGE };
      if (searchDebounced) params.search = searchDebounced;
      if (typeFilter === 'client') params.is_client = true;
      if (typeFilter === 'contractor') params.is_client = false;
      const res = await companiesApi.getAll(params);
      setCompanies(res.data.items || []);
      setTotalPages(res.data.total_pages || 1);
      setTotalItems(res.data.total || 0);
    } catch (error) {
      toast.error('Error al cargar empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate RUT
    if (formData.rut) {
      const rutCheck = validateRut(formData.rut);
      if (!rutCheck.valid) {
        toast.error(rutCheck.error);
        return;
      }
      formData.rut = rutCheck.formatted;
    }
    try {
      const data = { ...formData };
      if (!data.client_id) delete data.client_id;
      
      if (editingCompany) {
        await companiesApi.update(editingCompany.id, data);
        toast.success('Empresa actualizada');
      } else {
        await companiesApi.create(data);
        toast.success('Empresa creada');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar empresa');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      trade_name: '',
      rut: '',
      address: '',
      phone: '',
      email: '',
      is_client: false,
      client_id: ''
    });
    setEditingCompany(null);
  };

  const openEditDialog = (company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name,
      trade_name: company.trade_name || '',
      rut: company.rut || '',
      address: company.address || '',
      phone: company.phone || '',
      email: company.email || '',
      is_client: company.is_client,
      client_id: company.client_id || ''
    });
    setIsDialogOpen(true);
  };

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [searchDebounced, typeFilter]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const pageIds = companies.map(c => c.id);
    const allSelected = pageIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      pageIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="companies-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#2B4B8C]">Empresas</h1>
          <p className="text-[#2B4B8C]/60">Gestiona mandantes y contratistas</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={() => setIsBulkUploadOpen(true)} 
            className="border-[#2B4B8C]/20 text-[#2B4B8C] hover:bg-[#2B4B8C]/5"
            data-testid="bulk-upload-companies-btn"
          >
            <UploadSimple className="mr-2" size={20} />
            Carga Masiva
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="create-company-btn">
              <Plus className="mr-2" size={20} />
              Nueva Empresa
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-[#2B4B8C]/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-[#2B4B8C]">
                {editingCompany ? 'Editar Empresa' : 'Nueva Empresa'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#2B4B8C]/80">Nombre</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#2B4B8C]/80">Nombre Fantasía</Label>
                  <Input
                    value={formData.trade_name}
                    onChange={(e) => setFormData({ ...formData, trade_name: e.target.value })}
                    className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                    placeholder="Nombre comercial"
                    data-testid="company-trade-name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#2B4B8C]/80">RUT</Label>
                  <Input
                    value={formData.rut}
                    onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
                    className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                    placeholder="76.XXX.XXX-X"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#2B4B8C]/80">Teléfono</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">Dirección</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-[#f8fafc]">
                <div>
                  <Label className="text-[#2B4B8C]/80">Es Mandante (Cliente)</Label>
                  <p className="text-xs text-[#2B4B8C]/50">Las empresas mandantes contratan capacitaciones</p>
                </div>
                <Switch
                  checked={formData.is_client}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_client: checked, client_id: '' })}
                />
              </div>
              {!formData.is_client && clients.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-[#2B4B8C]/80">Mandante Asociado</Label>
                  <Select value={formData.client_id} onValueChange={(value) => setFormData({ ...formData, client_id: value })}>
                    <SelectTrigger className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]">
                      <SelectValue placeholder="Seleccionar mandante" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#2B4B8C]/10">
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1 border-[#2B4B8C]/10">
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 bg-[#2BBAA4] text-black hover:bg-[#239986]">
                  {editingCompany ? 'Actualizar' : 'Crear'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Bulk Upload Dialog - Companies */}
      <BulkUploadDialog
        open={isBulkUploadOpen}
        onOpenChange={setIsBulkUploadOpen}
        title="Carga Masiva de Empresas"
        templateHeaders={['Nombre', 'Rut', 'Dirección', 'Teléfono', 'Email', 'Es Cliente']}
        fieldNames={['name', 'rut', 'address', 'phone', 'email', 'is_client']}
        templateExample={['Empresa Ejemplo', '76.123.456-7', 'Av. Principal 123', '+56912345678', 'empresa@email.com', 'false']}
        onUpload={async (data) => {
          const payload = { companies: data.map(row => ({
            name: row.name,
            rut: row.rut || null,
            address: row.address || null,
            phone: row.phone || null,
            email: row.email || null,
            is_client: row.is_client === 'true' || row.is_client === 'si' || row.is_client === '1'
          }))};
          const res = await companiesApi.bulkUpload(payload);
          fetchData();
          return res.data;
        }}
        entityName="empresas"
      />

      {/* Filters */}
      <Card className="bg-white border-[#2B4B8C]/10">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2B4B8C]/60" size={20} />
              <Input
                placeholder="Buscar por nombre o RUT..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]">
                <SelectValue placeholder="Filtrar por tipo" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#2B4B8C]/10">
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="client">Mandantes</SelectItem>
                <SelectItem value="contractor">Contratistas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Delete Bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between p-3 rounded-lg bg-red-50 border border-red-200 animate-fade-in" data-testid="bulk-delete-bar-companies">
          <span className="text-sm font-medium text-red-700">
            {selectedIds.size} empresa(s) seleccionada(s)
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={clearSelection} className="border-red-200 text-red-600 hover:bg-red-50 h-8" data-testid="clear-selection-companies-btn">
              Deseleccionar
            </Button>
            <Button size="sm" onClick={() => setShowBulkDelete(true)} className="bg-red-500 text-white hover:bg-red-600 h-8" data-testid="bulk-delete-companies-btn">
              <Trash size={14} className="mr-1" /> Eliminar Seleccionadas
            </Button>
          </div>
        </div>
      )}

      {/* Companies Table */}
      <Card className="bg-white border-[#2B4B8C]/10">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#2B4B8C]/10 hover:bg-transparent">
                <TableHead className="w-10">
                  <Checkbox
                    checked={companies.length > 0 && companies.every(c => selectedIds.has(c.id))}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-companies-checkbox"
                  />
                </TableHead>
                <TableHead className="text-[#2B4B8C]/60 w-16">ID</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Empresa</TableHead>
                <TableHead className="text-[#2B4B8C]/60">RUT</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Nombre Fantasía</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Tipo</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Trabajadores</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Estado</TableHead>
                <TableHead className="text-[#2B4B8C]/60 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {companies.map((company) => (
                <TableRow key={company.id} className={`border-[#2B4B8C]/5 hover:bg-[#f8fafc] ${selectedIds.has(company.id) ? 'bg-red-50/50' : ''}`}>
                  <TableCell>
                    <Checkbox
                      checked={selectedIds.has(company.id)}
                      onCheckedChange={() => toggleSelect(company.id)}
                      data-testid={`select-company-${company.id}`}
                    />
                  </TableCell>
                  <TableCell className="text-[#2B4B8C]/40 font-mono text-xs" data-testid={`company-id-${company.id}`}>{company.external_id || '-'}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#2B4B8C]/20 flex items-center justify-center">
                        <Buildings size={20} className="text-[#2B4B8C]" />
                      </div>
                      <span className="text-[#2B4B8C] font-medium">{company.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-[#2B4B8C]/60 font-mono text-sm">{company.rut || '-'}</TableCell>
                  <TableCell className="text-[#2B4B8C]/60">{company.trade_name || '-'}</TableCell>
                  <TableCell>
                    <Badge className={company.is_client ? 'bg-orange-500/10 text-orange-400 border-orange-500/20 border' : 'bg-blue-500/10 text-blue-400 border-blue-500/20 border'}>
                      {company.is_client ? 'Mandante' : 'Contratista'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-[#2B4B8C]/60">
                      <Users size={16} />
                      <span>{company.workers_count || 0}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={company.is_active ? 'bg-green-500/10 text-green-400 border-green-500/20 border' : 'bg-red-500/10 text-red-400 border-red-500/20 border'}>
                      {company.is_active ? 'Activa' : 'Inactiva'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/admin/companies/${company.id}`)}
                        className="text-[#2BBAA4] border-[#2BBAA4]/30 hover:bg-[#2BBAA4]/5 text-xs h-8 px-3"
                        data-testid={`view-company-${company.id}`}
                      >
                        <Eye size={14} className="mr-1" /> VER
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(company)} className="text-[#2B4B8C]/60 hover:text-[#2B4B8C]">
                        <Pencil size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeletingCompany(company)} className="text-red-400 hover:text-red-500" data-testid={`delete-company-${company.id}`}>
                        <Trash size={16} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {companies.length === 0 && !loading && (
            <div className="text-center py-12 text-[#2B4B8C]/50">
              No se encontraron empresas
            </div>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-[#2B4B8C]/10">
              <span className="text-sm text-[#2B4B8C]/60">
                Mostrando {(currentPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} de {totalItems}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                  className="border-[#2B4B8C]/10 text-[#2B4B8C]"
                  data-testid="companies-prev-page"
                >
                  <CaretLeft size={16} className="mr-1" /> Anterior
                </Button>
                <span className="text-sm text-[#2B4B8C] font-medium px-2">
                  {currentPage} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                  className="border-[#2B4B8C]/10 text-[#2B4B8C]"
                  data-testid="companies-next-page"
                >
                  Siguiente <CaretRight size={16} className="ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <DeleteConfirmDialog
        open={!!deletingCompany}
        onOpenChange={(open) => { if (!open) setDeletingCompany(null); }}
        entityName="Empresa"
        itemName={deletingCompany?.name || ''}
        fetchDependencies={deletingCompany ? () => companiesApi.getDependencies(deletingCompany.id) : null}
        onConfirmDelete={async () => {
          await companiesApi.delete(deletingCompany.id);
          toast.success('Empresa eliminada');
          setDeletingCompany(null);
          fetchData();
        }}
      />

      <BulkDeleteConfirmDialog
        open={showBulkDelete}
        onOpenChange={setShowBulkDelete}
        entityName="empresa(s)"
        count={selectedIds.size}
        fetchDependencies={() => companiesApi.bulkDependencies([...selectedIds])}
        onConfirmDelete={async () => {
          await companiesApi.bulkDelete([...selectedIds]);
          toast.success(`${selectedIds.size} empresa(s) eliminada(s)`);
          clearSelection();
          fetchData();
        }}
      />
    </div>
  );
}
